import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader } from '@design-system/Sheet';
import { Image } from '@design-system/Image';
import { X } from 'lucide-react';
import { ScoreCard } from '@design-system/Cards/ScoreCard';
import { FoodAndIngredientsList } from './FoodAndIngredientsList';
import { getL2ImageUrl } from '@/utils/nutrition/getImageUrl';
import { HealthSummary } from '@viome/viome-base/src/usecase/Home/Home.model';
import { getRankColor } from '@utils/getViomeColor.ts';
import { useNavigate } from 'react-router-dom';
import { DetectedIcon } from '@/components/icons/DetectedIcon';
import { SelfReportedIcon } from '@/components/icons/SelfReportedIcon';

interface HealthInsightSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  insight: HealthSummary;
}

const GRADIENT_STYLES = {
  detected: 'bg-[linear-gradient(180deg,#051559_0%,#2A4B99_47%,#91A0D0_100%)]',
  other: 'bg-[linear-gradient(180deg,#003351_0%,#007F96_50%,#36B8BB_100%)]',
};

export const HealthInsightSheet: React.FC<HealthInsightSheetProps> = ({
  isOpen,
  onOpenChange,
  insight,
}) => {
  const isDetected = insight.header.toLowerCase() === 'detected';
  const navigate = useNavigate();
  const gradientStyle = isDetected
    ? GRADIENT_STYLES.detected
    : GRADIENT_STYLES.other;

  const [hasFoodData, setHasFoodData] = useState<boolean>(true);

  const handleFoodDataAvailability = (hasData: boolean) => {
    setHasFoodData(hasData);
  };

  const renderGradientContent = () => (
    <>
      <div className="flex items-center gap-2 mt-12">
        {isDetected ? (
          <DetectedIcon width={24} height={24} />
        ) : (
          <SelfReportedIcon width={24} height={24} />
        )}
        <span className="typography-caption1 text-white/50">
          {insight.header}
        </span>
      </div>

      <SheetHeader className="!p-0">
        <h2 className="typography-h1 text-white mt-4">{insight.title}</h2>
      </SheetHeader>

      <hr className="w-full border-t border-[rgba(255,255,255,0.2)] mt-3 mb-6" />

      {insight.tag && (
        <div className="mb-2">
          <span className="typography-h3 text-white">{insight.tag}</span>
        </div>
      )}

      <p className="typography-body text-white">{insight.description}</p>
      <div className="flex items-center text-white my-6">
        <p className="typography-body">Related To</p>
        <Image
          width={36}
          height={36}
          aria-hidden
          src={getL2ImageUrl(insight.related_to_icon)}
        />
        <p className="typography-body">{insight.related_to}</p>
      </div>
      <div className="flex flex-col gap-4">
        <p className="typography-body text-white">
          Track this score and learn more about this activity’s impact on your health.
        </p>
        <div className="[&>button]:!bg-[#000000] [&>button]:opacity-100">
          {insight.score !== null && (
            <ScoreCard
              score={insight.score}
              tag={insight.rank || ''}
              title={insight.score_name || ''}
              color={getRankColor(insight.rank || '')}
              variant="dark"
              onClick={() => navigate(`/health/l3/${insight.score_id}`)}
            />
          )}
        </div>
      </div>
    </>
  );

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        className="!p-0 !overflow-hidden !border-none !bg-transparent"
        variant="default"
      >
        <button
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary z-50"
        >
          <X className="h-4 w-4 md:h-6 md:w-6 text-white" />
        </button>

        {hasFoodData ? (
          <div className="h-full overflow-auto">
            <div className="flex flex-col h-full">
              <div
                className={`relative flex-shrink-0 ${gradientStyle} backdrop-blur-[50px] shadow-[inset_0px_1px_0px_0px_#FFFFFF0A,0px_1px_3px_0px_#0000001A] p-6`}
              >
                {renderGradientContent()}
              </div>

              <div className="bg-gray-50 flex-grow p-6">
                <FoodAndIngredientsList
                  scoreId={insight.score_id}
                  reportType={insight.header}
                  conditionName={insight.title}
                  isDetected={isDetected}
                  onDataAvailability={handleFoodDataAvailability}
                />
              </div>
            </div>
          </div>
        ) : (
          <div
            className={`h-full ${gradientStyle} backdrop-blur-[50px] shadow-[inset_0px_1px_0px_0px_#FFFFFF0A,0px_1px_3px_0px_#0000001A] p-6 overflow-auto`}
          >
            {renderGradientContent()}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
